/**
 * FullPageTabs - Tab navigation for full-page view's right column.
 * Supports Overview, Activity, Emails, Notes, Files, Associations, Meeting Prep, and Intelligence tabs with ARIA accessibility.
 */

import {
  Activity,
  BookOpen01,
  Calendar,
  CheckSquare,
  File02,
  File04,
  Hash01,
  Mail01,
  Menu02,
  Stars02,
  Users01,
} from '@untitledui/icons';
import {
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import type { OverflowItem } from '@/lib/base/tabs';
import { OverflowBar, useOverflow } from '@/lib/base/tabs';

import type { FullPageTabId } from '../registry/entityTypeRegistry';

export type FullPageTabType = FullPageTabId;

type TabIcon = FC<{ className?: string; 'aria-hidden'?: 'true' }>;

/** Per-tab leading icon (Lightfield-style icon + label tabs). */
function getTabIcon(tab: FullPageTabType): TabIcon {
  switch (tab) {
    case 'overview':
      return Menu02;
    case 'activity':
      return Activity;
    case 'contacts':
      return Users01;
    case 'emails':
      return Mail01;
    case 'meetings':
      return Calendar;
    case 'prep':
      return BookOpen01;
    case 'notes':
      return File02;
    case 'tasks':
      return CheckSquare;
    case 'files':
      return File04;
    case 'intelligence':
      return Stars02;
    case 'associations':
    default:
      return Hash01;
  }
}

export interface FullPageTabsProps {
  /** Overview tab content (added in Issue 7) */
  overviewContent?: ReactNode;

  /** Activity tab content (timeline) */
  activityContent?: ReactNode;

  /** Contacts tab content — list of associated `ahoy_contact` entities */
  contactsContent?: ReactNode;

  /** Emails tab content */
  emailsContent?: ReactNode;

  /** Meetings tab content — list of associated `ahoy_meeting` entities */
  meetingsContent?: ReactNode;

  /** Notes tab content */
  notesContent?: ReactNode;
  /**
   * The record's Tasks tab. Passed only once the org has migrated to
   * first-class tasks — an unmigrated org has no `ahoy_task` in its schema, so
   * omitting the prop drops the tab entirely and the record looks exactly as
   * it does today.
   */
  tasksContent?: ReactNode;

  /** Files tab content */
  filesContent?: ReactNode;

  /** Associations tab content (added in Issue 7) */
  associationsContent?: ReactNode;

  /** Meeting Prep tab content (optional, for meetings only) */
  prepContent?: ReactNode;

  /** Intelligence tab content (optional, maps to Research) */
  intelligenceContent?: ReactNode;

  /** Default active tab */
  defaultTab?: FullPageTabType;

  /** Callback when active tab changes */
  onTabChange?: (tab: FullPageTabType) => void;

  /**
   * Optional per-tab trailing-action slot rendered on the right side of
   * the tab strip when there's horizontal room. Use this for tab-scoped
   * controls (e.g. the Activity tab's filter button) that would
   * otherwise have to live in their own row below the tab strip and
   * push tab content down. Receives the currently-active tab so
   * consumers can render context-appropriate actions.
   */
  tabActions?: (activeTab: FullPageTabType) => ReactNode;

  /**
   * Content rendered at the left of the tab strip row (e.g. the record
   * identity: avatar + name). Lets the strip act as a single top bar.
   */
  leading?: ReactNode;

  /**
   * Content rendered at the right of the tab strip row (e.g. record action
   * buttons). Sits alongside any per-tab `tabActions`.
   */
  trailing?: ReactNode;

  /**
   * Tabs whose content manages its own height + scroll (e.g. an embedded
   * virtualized list table). For these the panel becomes a bounded flex
   * container instead of the default padded `overflow-y-auto` scroll area, so
   * the content can use `flex-1 min-h-0` to fill the available height.
   */
  fillTabs?: ReadonlySet<FullPageTabType>;
}

function getTabLabel(tab: FullPageTabType, hasMeetingPrep: boolean): string {
  switch (tab) {
    case 'overview':
      return 'Overview';
    case 'activity':
      return 'Activity';
    case 'contacts':
      return 'Contacts';
    case 'emails':
      return 'Emails';
    case 'meetings':
      return 'Meetings';
    case 'prep':
      return 'Meeting Prep';
    case 'notes':
      return hasMeetingPrep ? 'Meeting Summaries' : 'Notes';
    case 'tasks':
      return 'Tasks';
    case 'files':
      return 'Files';
    case 'associations':
      return 'Associations';
    case 'intelligence':
      return 'Intelligence';
    default:
      return tab;
  }
}

/**
 * Tab navigation component for full-page record view.
 */
export function FullPageTabs({
  overviewContent,
  activityContent,
  contactsContent,
  emailsContent,
  meetingsContent,
  notesContent,
  tasksContent,
  filesContent,
  associationsContent,
  prepContent,
  intelligenceContent,
  defaultTab = 'overview',
  onTabChange,
  tabActions,
  leading,
  trailing,
  fillTabs,
}: FullPageTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const [shouldFocusPanel, setShouldFocusPanel] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const overflowTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Build available tabs array
  const availableTabs: FullPageTabType[] = useMemo(() => {
    const tabs: FullPageTabType[] = [];
    if (overviewContent) {
      tabs.push('overview');
    }
    if (activityContent) {
      tabs.push('activity');
    }
    if (contactsContent) {
      tabs.push('contacts');
    }
    if (emailsContent) {
      tabs.push('emails');
    }
    if (meetingsContent) {
      tabs.push('meetings');
    }
    if (prepContent) {
      tabs.push('prep');
    }
    // Tasks before Notes: a record's outstanding work is what a
    // user reaches for first.
    if (tasksContent) {
      tabs.push('tasks');
    }
    if (notesContent) {
      tabs.push('notes');
    }
    if (filesContent) {
      tabs.push('files');
    }
    if (associationsContent) {
      tabs.push('associations');
    }
    if (intelligenceContent) {
      tabs.push('intelligence');
    }
    return tabs;
  }, [
    overviewContent,
    activityContent,
    contactsContent,
    emailsContent,
    meetingsContent,
    prepContent,
    tasksContent,
    notesContent,
    filesContent,
    associationsContent,
    intelligenceContent,
  ]);

  // Derive activeTab from URL params with validation
  const activeTab = useMemo(() => {
    const rawUrlTab = searchParams.get('tab');
    // Back-compat: ?tab=timeline aliases to ?tab=activity
    const urlTab = (
      rawUrlTab === 'timeline' ? 'activity' : rawUrlTab
    ) as FullPageTabType | null;

    // Validate that the URL tab is available
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }

    // Fall back to default tab if it's available, otherwise first available tab
    if (availableTabs.includes(defaultTab)) {
      return defaultTab;
    }
    return availableTabs[0];
  }, [searchParams, availableTabs, defaultTab]);

  const handleTabChange = useCallback(
    (tab: FullPageTabType, moveFocusToPanel = false) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', tab);
      setSearchParams(newParams, { replace: true });
      onTabChange?.(tab);

      // Scroll the new tab back to the top. Defer to the next frame so
      // the reset runs after React has swapped in the new tab panel —
      // setting scrollTop synchronously here was getting clobbered by
      // the panel's own first paint on some browsers, leaving the
      // page on the previous tab's scroll offset.
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
        // Belt-and-braces: on viewports below the `lg` breakpoint the
        // CRMObjectFullPageView main container is `min-h-full` instead
        // of fixed-height, so it's the window that scrolls. Reset
        // both so the user lands at the top regardless of layout mode.
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
      });

      // Set flag to move focus after render completes
      if (moveFocusToPanel) {
        setShouldFocusPanel(true);
      }
    },
    [searchParams, setSearchParams, onTabChange]
  );

  // Callback ref that focuses element when mounted if flag is set
  const getPanelRef = useCallback(
    (index: number) => {
      return (el: HTMLDivElement | null) => {
        panelRefs.current[index] = el;
        if (el && shouldFocusPanel) {
          // Use queueMicrotask to ensure focus happens after React finishes rendering
          queueMicrotask(() => {
            el.focus();
            setShouldFocusPanel(false);
          });
        }
      };
    },
    [shouldFocusPanel]
  );

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      setIsScrolled(scrollRef.current.scrollTop > 0);
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let newIndex: number | null = null;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newIndex =
            currentIndex > 0 ? currentIndex - 1 : availableTabs.length - 1;
          break;
        case 'ArrowRight':
          event.preventDefault();
          newIndex =
            currentIndex < availableTabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = availableTabs.length - 1;
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleTabChange(availableTabs[currentIndex]);
          return;
        default:
          return;
      }

      if (newIndex !== null) {
        handleTabChange(availableTabs[newIndex]);
        const targetTab = tabRefs.current[newIndex];
        if (targetTab) {
          targetTab.focus();
        } else {
          // Target is collapsed into the overflow menu (strict order keeps it
          // there) — move focus to the kebab so it stays keyboard-reachable.
          overflowTriggerRef.current?.focus();
        }
      }
    },
    [handleTabChange, availableTabs]
  );

  // Responsive overflow: collapse tabs that don't fit into a kebab menu.
  const {
    containerRef: tabsContainerRef,
    setItemRef,
    visibleCount,
  } = useOverflow(availableTabs.length);

  const overflowItems = useMemo<OverflowItem[]>(
    () =>
      availableTabs.map((tab, index) => {
        const Icon = getTabIcon(tab);
        const isActive = activeTab === tab;
        return {
          key: tab,
          render: () => (
            <button
              ref={el => {
                tabRefs.current[index] = el;
              }}
              type='button'
              role='tab'
              id={`fullpage-tab-${tab}`}
              aria-selected={isActive}
              aria-controls={`fullpage-panel-${tab}`}
              aria-describedby={`${tab}-tab-description`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTabChange(tab, true)}
              onKeyDown={e => handleKeyDown(e, index)}
              className={`inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                isActive
                  ? 'bg-[var(--color-bg-secondary)] text-fg-primary'
                  : 'text-fg-tertiary hover:text-fg-secondary hover:bg-[var(--color-bg-secondary)]'
              }`}
            >
              <Icon className='size-3 shrink-0' aria-hidden='true' />
              {getTabLabel(tab, !!prepContent)}
            </button>
          ),
          menu: {
            label: getTabLabel(tab, !!prepContent),
            icon: Icon,
            isActive,
            onSelect: () => handleTabChange(tab, true),
          },
        };
      }),
    [availableTabs, activeTab, prepContent, handleTabChange, handleKeyDown]
  );

  const activeTabOverflowed = availableTabs.indexOf(activeTab) >= visibleCount;

  const showTabs = availableTabs.length > 1;
  // When the active tab manages its own height/scroll (e.g. an embedded list
  // table), the content area is a bounded flex container rather than the
  // default padded scroll area.
  const isFillTab = activeTab != null && (fillTabs?.has(activeTab) ?? false);

  return (
    <div className='h-full flex flex-col relative'>
      {/* Screen reader descriptions for tabs */}
      <div id='overview-tab-description' className='sr-only'>
        View record overview and highlights
      </div>
      <div id='activity-tab-description' className='sr-only'>
        View activity timeline and event history
      </div>
      <div id='contacts-tab-description' className='sr-only'>
        View contacts associated with this record
      </div>
      <div id='emails-tab-description' className='sr-only'>
        View email events for this record
      </div>
      <div id='meetings-tab-description' className='sr-only'>
        View meetings associated with this record
      </div>
      <div id='prep-tab-description' className='sr-only'>
        View meeting prep brief
      </div>
      <div id='notes-tab-description' className='sr-only'>
        View and edit notes for this record
      </div>
      <div id='tasks-tab-description' className='sr-only'>
        View and edit tasks for this record
      </div>
      <div id='files-tab-description' className='sr-only'>
        View files attached to this record
      </div>
      <div id='associations-tab-description' className='sr-only'>
        View related records associated with this record
      </div>
      <div id='intelligence-tab-description' className='sr-only'>
        View AI-generated research and insights
      </div>

      {/* Top bar — a single slim row holding the record identity (`leading`),
          the tab strip, and the record actions (`trailing` + per-tab
          `tabActions`), with a bottom border separating it from the panel
          content. Mirrors Lightfield's compact top bar. Rendered whenever
          there's a tab strip OR slot content to show. */}
      {(showTabs || leading || trailing) && (
        <div
          className={`flex items-center gap-3 shrink-0 lg:-mx-6 lg:px-4${
            // The bar uses ONE horizontal treatment on every tab so it never
            // shifts when switching tabs: bleed it across the main column's
            // `px-6` to the full column width (nav edge → rail border) and
            // re-pad to `px-4` (16px) — identical to the embedded table's
            // toolbar. This pins the avatar at 16px (aligned with the Search
            // icon) on all tabs, and makes any divider here span full width.
            //
            // Vertical: the divider below the bar must sit at the SAME height
            // on every tab. On fill tabs the divider is the embedded table
            // toolbar's `border-t`, which sits at `pb-2.5` + the embedded
            // header's `pt-1.5` (6px) below the tabs. Non-fill tabs draw their
            // own `border-b` directly at the bar's bottom, so they add that
            // same 6px back (`pb-4` = pb-2.5 + 6px) to land the line at the
            // identical height. Fill tabs drop the border (the table's own
            // `border-t` is the divider, matching a standalone list page).
            isFillTab
              ? ' pb-2.5'
              : ' pb-4 border-b border-[var(--color-border-secondary)]'
          }`}
        >
          {leading && (
            <div className='flex min-w-0 items-center'>{leading}</div>
          )}
          {showTabs && (
            <OverflowBar
              items={overflowItems}
              visibleCount={visibleCount}
              containerRef={tabsContainerRef}
              setItemRef={setItemRef}
              triggerActive={activeTabOverflowed}
              triggerRef={overflowTriggerRef}
              containerRole='tablist'
              ariaLabel='Record tabs'
              className='min-w-0 flex-1'
              // Match the slideout ("sidebar") record-actions kebab: same p-2
              // box + hover fill and the default 20px DotsVertical icon, so the
              // overflow kebab is the same size as the record-actions kebab.
              triggerClassName='p-2 hover:bg-[var(--color-bg-tertiary)] rounded-lg transition-colors cursor-pointer text-fg-quaternary'
            />
          )}
          {/* When the active tab is collapsed into the overflow menu its
              `role="tab"` button is not rendered, so the active panel's
              `aria-labelledby` would dangle. Provide the referenced id as an
              sr-only label element instead. */}
          {showTabs && activeTabOverflowed && (
            <span id={`fullpage-tab-${activeTab}`} className='sr-only'>
              {getTabLabel(activeTab, !!prepContent)}
            </span>
          )}
          {(tabActions || trailing) && (
            <div className='flex items-center gap-1 shrink-0 ml-auto'>
              {tabActions && activeTab && tabActions(activeTab)}
              {trailing}
            </div>
          )}
        </div>
      )}

      {/* Tab Content. Default: a padded, scrollable area. Fill tabs: a bounded
          flex container so embedded content (e.g. a virtualized list table)
          can fill the height and own its scroll. */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={
          isFillTab
            ? 'flex-1 min-h-0 flex flex-col'
            : `flex-1 overflow-y-auto pt-4 pb-12 md:pb-16 lg:pb-20 transition-shadow duration-200 ${
                isScrolled ? 'shadow-inner' : ''
              }`
        }
      >
        {activeTab === 'overview' && overviewContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('overview'))}
            role='tabpanel'
            id='fullpage-panel-overview'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-overview' })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {overviewContent}
          </div>
        )}
        {activeTab === 'activity' && activityContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('activity'))}
            role='tabpanel'
            id='fullpage-panel-activity'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-activity' })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {activityContent}
          </div>
        )}
        {activeTab === 'contacts' && contactsContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('contacts'))}
            role='tabpanel'
            id='fullpage-panel-contacts'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-contacts' })}
            tabIndex={-1}
            className={
              isFillTab
                ? 'flex-1 min-h-0 flex flex-col focus-visible:outline-none'
                : 'focus-visible:outline-none'
            }
          >
            {contactsContent}
          </div>
        )}
        {activeTab === 'emails' && emailsContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('emails'))}
            role='tabpanel'
            id='fullpage-panel-emails'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-emails' })}
            tabIndex={-1}
            className={
              isFillTab
                ? 'flex-1 min-h-0 flex flex-col focus-visible:outline-none'
                : 'focus-visible:outline-none'
            }
          >
            {emailsContent}
          </div>
        )}
        {activeTab === 'meetings' && meetingsContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('meetings'))}
            role='tabpanel'
            id='fullpage-panel-meetings'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-meetings' })}
            tabIndex={-1}
            className={
              isFillTab
                ? 'flex-1 min-h-0 flex flex-col focus-visible:outline-none'
                : 'focus-visible:outline-none'
            }
          >
            {meetingsContent}
          </div>
        )}
        {activeTab === 'prep' && prepContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('prep'))}
            role='tabpanel'
            id='fullpage-panel-prep'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-prep' })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {prepContent}
          </div>
        )}
        {activeTab === 'notes' && notesContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('notes'))}
            role='tabpanel'
            id='fullpage-panel-notes'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-notes' })}
            tabIndex={-1}
            className={
              isFillTab
                ? 'flex-1 min-h-0 flex flex-col focus-visible:outline-none'
                : 'focus-visible:outline-none'
            }
          >
            {notesContent}
          </div>
        )}
        {activeTab === 'tasks' && tasksContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('tasks'))}
            role='tabpanel'
            id='fullpage-panel-tasks'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-tasks' })}
            tabIndex={-1}
            className={
              isFillTab
                ? 'flex-1 min-h-0 flex flex-col focus-visible:outline-none'
                : 'focus-visible:outline-none'
            }
          >
            {tasksContent}
          </div>
        )}
        {activeTab === 'files' && filesContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('files'))}
            role='tabpanel'
            id='fullpage-panel-files'
            {...(showTabs && { 'aria-labelledby': 'fullpage-tab-files' })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {filesContent}
          </div>
        )}
        {activeTab === 'associations' && associationsContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('associations'))}
            role='tabpanel'
            id='fullpage-panel-associations'
            {...(showTabs && {
              'aria-labelledby': 'fullpage-tab-associations',
            })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {associationsContent}
          </div>
        )}
        {activeTab === 'intelligence' && intelligenceContent && (
          <div
            ref={getPanelRef(availableTabs.indexOf('intelligence'))}
            role='tabpanel'
            id='fullpage-panel-intelligence'
            {...(showTabs && {
              'aria-labelledby': 'fullpage-tab-intelligence',
            })}
            tabIndex={-1}
            className='focus-visible:outline-none'
          >
            {intelligenceContent}
          </div>
        )}
      </div>
    </div>
  );
}
